/* External */
import { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import CommonCollectionCard from '@dtvgo/collection-card';

/* Theme Assets */
import Logo from '~/assets/theme/svg/brand-logo.svg';

/* Utils */
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { LANDER_LOGO_BASE_IMAGE_URL, ImageRatio, SKY_MAIS_TITLE } from '../../utils/constants';
import { CardType, CardWidth } from '../../utils/cardsUtils';
import { getJsonImage } from '../../theme/images';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

/* Styles */
import './CollectionCard.scss';

const IMG_WIDTH = CardWidth[CardType.COLLECTION] + 20;
const IMG_DIMENSION = ImageRatio.ONE_BY_ONE;

const CHANNEL_LOGO_IMPORTS = import.meta.glob('../../assets/images/*.{png,jpg,jpeg,svg}', {
  eager: true,
  import: 'default',
});

function normalizeChannelNameForLogo(value = '') {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .replace(/[^\da-z]/g, '');
}

function getChannelLogoSource(title = '') {
  const normalizedTitle = normalizeChannelNameForLogo(title);

  const logoEntry = Object.entries(CHANNEL_LOGO_IMPORTS).find(([path]) => {
    const basename = path.split('/').pop().replace(/\.[^./]+$/, '');
    return normalizeChannelNameForLogo(basename) === normalizedTitle;
  });

  return logoEntry ? logoEntry[1] : undefined;
}

function CollectionCard({
  className = '',
  item: collection,
  focused,
  onMouseDown,
  onMouseEnter,
}) {
  const { title, cardImages } = collection;
  let displayedTitle = title;
  const [isErrorLanderLogo, setIsErrorLanderLogo] = useState(false);
  const { imageUrl } = useImageCard({
    images: cardImages,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const [isErrorAssetLogo, setIsErrorAssetLogo] = useState(false);
  const defaultLogoSrc = getChannelLogoSource(displayedTitle);

  const handleLanderLogoError = useCallback(() => {
    setIsErrorLanderLogo(true);
  }, []);

  const handleAssetLogoError = useCallback(() => {
    setIsErrorAssetLogo(true);
  }, []);

  if (displayedTitle.toUpperCase() === SKY_MAIS_TITLE.toUpperCase()) {
    displayedTitle = SKY_MAIS_TITLE;
  }

  let collectionCardContent;

  if (displayedTitle === SKY_MAIS_TITLE) {
    collectionCardContent = (
      <span className="dtv-common-c-collection-card__lander-logo dtv-html5-c-collection-card__lander-logo">
        <img className="dtv-common-c-collection-card__lander-logo-img" src={getJsonImage('brand-logo') || Logo} alt="Sky" />
      </span>
    );
  } else if (!isErrorLanderLogo) {
    collectionCardContent = (
      <CommonCollectionCard.LanderLogo
        className="dtv-html5-c-collection-card__lander-logo"
        title="-"
        baseImageUrl={LANDER_LOGO_BASE_IMAGE_URL}
        onError={handleLanderLogoError}
      />
    );
  } else if (!isErrorAssetLogo && defaultLogoSrc) {
    collectionCardContent = (
      <span className="dtv-common-c-collection-card__lander-logo dtv-html5-c-collection-card__lander-logo">
        <img
          className="dtv-common-c-collection-card__lander-logo-img"
          src={defaultLogoSrc}
          alt={displayedTitle}
          onError={handleAssetLogoError}
        />
      </span>
    );
  } else {
    collectionCardContent = (
      <CommonCollectionCard.Title className={displayedTitle === SKY_MAIS_TITLE ? 'dtv-html5-c-collection-card__title-capitalized' : 'dtv-html5-c-collection-card__title'}>
        {displayedTitle || ''}
      </CommonCollectionCard.Title>
    );
  }

  return (
    <CommonCollectionCard
      className={`dtv-html5-c-collection-card ${focused ? 'dtv-html5-c-collection-card--focused' : ''
      } ${className}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <CommonCollectionCard.Image imageUrl={imageUrl} imageAlt={title} />
      {collectionCardContent}
    </CommonCollectionCard>
  );
}

CollectionCard.propTypes = {
  className: PropTypes.string,
  item: PropTypes.shape().isRequired,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(CollectionCard)));
